/**
 * DIE MAIL AN DEN EIGENTÜMER VOR DEM SENDEN ANSEHEN (.563).
 * ============================================================================
 *
 * ORIGINALTEXT MARVIN (30.10.2026)
 *   „da muss es die Vorschau geben, wie die Mail aussieht, wie sie verschickt
 *    wird, plus wie der Anhang aussieht, um das gegenzuprüfen, dass es richtig
 *    ist. Die PDF muss ja erzeugt werden."
 *
 * Links die Mail, wie sie rausgeht (An, Betreff, Text; Betreff und Text
 * lassen sich für diese eine Mail ändern), rechts der Anhang, gebaut von
 * derselben Stelle, die ihn auch mitschickt. Ohne `senden` nur zum Ansehen
 * (die Erinnerungen gehen vom Lauf).
 */
import { useEffect, useState } from 'react';
import { api } from '../api.ts';
import { Dialog, Fehlerkasten, ausFehler, type Fehlerlage } from './Formular.tsx';

export function MailVorschau({ auftragId, schluessel, titel, email, hinweis, zu, senden, spaeterText, sendenText }: {
  auftragId: string; schluessel: string; titel: string; email?: string | null; hinweis?: string;
  zu: () => void; senden?: (w: { email: string; betreff?: string; text?: string }) => Promise<any>;
  /** .587: Beschriftung von „Später“ und „Senden“, wenn das Fenster direkt nach dem Buchen kommt. */
  spaeterText?: string; sendenText?: string;
}) {
  const [d, setD] = useState<any>(null);
  const [an, setAn] = useState(email ?? '');
  const [betreff, setBetreff] = useState('');
  const [text, setText] = useState('');
  const [fehler, setFehler] = useState<Fehlerlage | null>(null);
  const [laeuft, setLaeuft] = useState(false);
  useEffect(() => {
    api.ne4HausMailVorschau(auftragId, schluessel, email || undefined)
      .then((x) => { setD(x); setAn(x.an ?? ''); setBetreff(x.betreff ?? ''); setText(x.text ?? ''); })
      .catch((f) => setFehler(ausFehler(f)));
  }, [auftragId, schluessel]); // eslint-disable-line react-hooks/exhaustive-deps
  const geaendert = !!d && (betreff !== d.betreff || text !== d.text);
  const hindernis = d?.hindernis && !(d.hindernis === 'keine Mailadresse' && an.trim()) ? d.hindernis : null;
  async function los() {
    if (!senden) return;
    setLaeuft(true); setFehler(null);
    try { await senden({ email: an.trim(), ...(geaendert ? { betreff, text } : {}) }); }
    catch (x) { setFehler(ausFehler(x)); } finally { setLaeuft(false); }
  }
  const pdf = (d?.anhaenge ?? []).find((x: any) => x.mime === 'application/pdf');
  return (
    <Dialog titel={titel} untertitel={senden ? 'Vorschau: so geht die Mail raus' : 'Vorschau'} breite={pdf ? 1280 : 1000} laeuft={laeuft} zu={zu}>
      <Fehlerkasten lage={fehler} />
      {!d && !fehler && <div className="leise">Mail und Anhang werden gebaut …</div>}
      {d && (
        <div className="ne4-mailvorschau" style={{ display: 'grid', gridTemplateColumns: pdf ? 'minmax(340px, 1fr) minmax(380px, 1.1fr)' : '1fr', gap: 16 }}>
          <div>
            {hinweis && <p className="klein leise" style={{ marginTop: 0 }}>{hinweis}</p>}
            {hindernis && <div className="hinweis fehler" style={{ marginBottom: 8 }}>So geht die Mail nicht: {hindernis}.</div>}
            <div className="feld"><label>An</label>
              <input type="email" value={an} disabled={!senden} onChange={(e) => setAn(e.target.value)} placeholder="Mailadresse des Eigentümers" />
              {senden && <div className="klein leise">Eine andere Adresse wird am Haus gespeichert.</div>}
            </div>
            <div className="feld"><label>Betreff</label>
              <input value={betreff} disabled={!senden} onChange={(e) => setBetreff(e.target.value)} />
            </div>
            <div className="feld"><label>Text</label>
              <textarea value={text} disabled={!senden} rows={Math.max(12, String(text ?? '').split('\n').reduce((n: number, z: string) => n + Math.max(1, Math.ceil(z.length / 95)), 0) + 1)} style={{ width: '100%', fontFamily: 'inherit', lineHeight: 1.45 }} onChange={(e) => setText(e.target.value)} />
              <div className="klein leise">Die Signatur hängt der Mailweg an. {geaendert ? 'Geändert: gilt nur für diese Mail, die Vorlage bleibt.' : 'Die Vorlage steht unter Konfiguration, Mailtexte.'}</div>
            </div>
            <div className="klein">
              <b>Anhang:</b>{' '}
              {(d.anhaenge ?? []).length
                ? d.anhaenge.map((x: any) => <span key={x.dateiname}>{x.dateiname} ({Math.max(1, Math.round(x.groesse / 1024))} KB), <a href={x.url} target="_blank" rel="noreferrer">in neuem Fenster</a></span>)
                : <span className="leise">keiner</span>}
              {d.aushang && <div className="leise">{d.aushang}</div>}
            </div>
          </div>
          {pdf && (
            <iframe title="Anhang" src={pdf.url} style={{ width: '100%', height: 'min(80vh, 900px)', border: '1px solid var(--rand)', borderRadius: 8, background: '#fff' }} />
          )}
        </div>
      )}
      <div className="reihe" style={{ gap: 8, justifyContent: 'flex-end', marginTop: 12 }}>
        <button type="button" className="knopf" onClick={zu}>{senden ? (spaeterText ?? 'Später') : 'Schließen'}</button>
        {senden && <button type="button" className="knopf haupt" disabled={laeuft || !d || !!hindernis || !an.trim()} onClick={() => void los()}>{sendenText ?? 'Senden'}</button>}
      </div>
    </Dialog>
  );
}
